import { useState } from 'react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { usePrivateProfile } from 'src/hooks/use-profile'
import { useRouting } from 'src/routes'
import { MatchesHeader } from 'src/screens/progression-report/Header'
import { SkillRadar } from 'src/screens/progression-report/SkillRadarChart'
import { Button, ButtonVariant } from 'src/system/ui/button/Button'

export function ProgressionReport() {
  return <PrivateProgressionReport />
}

function PrivateProgressionReport() {
  const res = usePrivateProfile()
  return <PrivateContent profile={res} />
}

function PrivateContent(props: {
  profile: ReturnType<typeof usePrivateProfile>
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { toProfile } = useRouting()
  const { data, loading, error } = props.profile

  // Always collapsed by default; the user opens the overview each visit.
  const [expanded, setExpanded] = useState(false)

  const isLoading = !data && loading

  const progressionReport = data?.profile?.csgo?.progressionReport
  const profileId = data?.profile?.id

  const hasProgressions = progressionReport
    ? (progressionReport?.assignmentProgressions?.length ?? 0) > 0
    : false

  const hasError = Boolean(error)
  // Gate the toggle button on !hasError too: errorPolicy 'all' can yield cached
  // data alongside an error, and SkillRadar renders nothing when hasError, which
  // would otherwise leave a button that expands to an empty card.
  const shouldRender = hasProgressions && !isLoading && !hasError

  // On the matches page there are no assignment cards to scroll to, so the
  // whole radar links through to the full profile instead.
  const goToProfile = profileId
    ? () => navigate(toProfile(profileId))
    : undefined

  return (
    <Box>
      <MatchesHeader
        isLoading={loading}
        hasError={hasError}
        hasProgressions={hasProgressions}
      />
      {shouldRender && (
        <>
          <ToggleButton
            type='button'
            fullWidth
            variant={expanded ? ButtonVariant.secondary : ButtonVariant.primary}
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded
              ? t('assessments:radar:hide')
              : t('assessments:radar:show')}
          </ToggleButton>
          {expanded && (
            <SkillRadar
              data={progressionReport}
              hasError={hasError}
              shouldRender={shouldRender}
              onClick={goToProfile}
            />
          )}
        </>
      )}
    </Box>
  )
}

// Lighter padding than the shared progression Box (which is tuned for the full
// profile report): the matches report is just a header + collapsed button, so
// it shouldn't leave a big gap above the match list.
const Box = styled.div`
  padding: ${(props) => `${props.theme.d.spacing[8]} 0 0 0`};
`

const ToggleButton = styled(Button)`
  margin-bottom: ${(props) => props.theme.d.spacing[5]};
  height: 64px;
  font-size: 20px;
  letter-spacing: 1px;
  text-transform: uppercase;
`
